import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from '../../../lib/format';

// Positions a dropdown panel relative to its trigger button, flipping it to
// open upward (anchored to the trigger's top edge via `bottom`) when there
// isn't enough room below — e.g. a select near the bottom of a modal — so
// it never gets clipped off-screen. Constrained to the enclosing modal's
// own bounds (not the full viewport) so on a short window it stays inside
// the dialog card instead of overshooting past its header into the page
// behind it; falls back to the viewport when not inside a modal.
function computeDropdownPos(triggerEl, width, preferredHeight = 260) {
  const rect = triggerEl.getBoundingClientRect();
  const container = triggerEl.closest('.modal-panel, [data-dropdown-bounds]');
  const bounds = container
    ? container.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight };

  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const spaceBelow = bounds.bottom - rect.bottom - 8;
  const spaceAbove = rect.top - bounds.top - 8;
  const openUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
  const maxHeight = Math.max(120, Math.min(preferredHeight, openUp ? spaceAbove : spaceBelow));
  return openUp
    ? { left, width, openUp, maxHeight, bottom: window.innerHeight - rect.top + 5, top: undefined }
    : { left, width, openUp, maxHeight, top: rect.bottom + 5, bottom: undefined };
}

// ── Searchable select (replaces native <select> for Area, Assignee, etc.) ──────
function SelOption({ label, isSelected, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '7px 14px', fontSize: 13, cursor: 'pointer',
        color: isSelected ? 'var(--brand)' : 'var(--text-secondary)',
        fontWeight: isSelected ? 600 : 400,
        background: hov || isSelected ? 'var(--bg-muted)' : 'transparent',
        whiteSpace: 'normal', lineHeight: '1.4', wordBreak: 'break-word',
      }}
    >
      {label}
    </div>
  );
}

export function SearchableSelect({ placeholder, value, onChange, options, groups, btnClass = 'tl-select-btn', disabled = false, disabledTooltip = '', showAll = true, dropdownWidth = 230, matchTriggerWidth = false, wrapStyle, title }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: dropdownWidth, openUp: false });
  const ref = useRef(null);
  const searchRef = useRef(null);

  const openDropdown = () => {
    if (disabled) return;
    if (!open && ref.current) {
      const w = matchTriggerWidth ? ref.current.getBoundingClientRect().width : dropdownWidth;
      setDropPos(computeDropdownPos(ref.current, w));
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    setTimeout(() => searchRef.current?.focus(), 0);
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    }
    function handleScroll(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      setOpen(false); setSearch('');
    }
    document.addEventListener('mousedown', handle);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const allFlat = [
    ...(options || []),
    ...(groups || []).flatMap(g => g.options),
  ];
  const selected = allFlat.find(o => String(o.value) === String(value));

  const q = search.toLowerCase().trim();

  const filteredOptions = (options || []).filter(o => !q || o.label.toLowerCase().includes(q));
  const filteredGroups = (groups || [])
    .map(g => ({ ...g, options: g.options.filter(o => !q || o.label.toLowerCase().includes(q)) }))
    .filter(g => g.options.length);

  const select = (val) => { onChange(val); setOpen(false); setSearch(''); };

  return (
    <div ref={ref} style={{ minWidth: 0, width: '100%', ...wrapStyle }}>
      <button
        type="button"
        className={`${btnClass}${value ? ' active' : ''}${disabled ? ' disabled' : ''}`}
        onClick={openDropdown}
        title={disabled ? disabledTooltip : title}
        style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
      >
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>

      {open && !disabled && (
        <div style={{
          position: 'fixed', top: dropPos.top, bottom: dropPos.bottom, left: dropPos.left, zIndex: 9999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, width: dropPos.width, maxHeight: dropPos.maxHeight,
          maxWidth: matchTriggerWidth ? dropPos.width : Math.max(dropdownWidth, 300),
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '7px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.4, flexShrink: 0 }}>
              <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              ref={searchRef}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)', width: '100%' }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {showAll && <SelOption label="All" isSelected={!value} onClick={() => select('')} />}
            {filteredOptions.map(o => (
              <SelOption key={o.value} label={o.label} isSelected={String(value) === String(o.value)} onClick={() => select(o.value)} />
            ))}
            {filteredGroups.map(g => (
              <React.Fragment key={g.label}>
                <div style={{ padding: '5px 14px 2px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {g.label}
                </div>
                {g.options.map(o => (
                  <SelOption key={o.value} label={o.label} isSelected={String(value) === String(o.value)} onClick={() => select(o.value)} />
                ))}
              </React.Fragment>
            ))}
            {filteredOptions.length === 0 && filteredGroups.length === 0 && (
              <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterDropdown({ label, options, value, onChange, btnClass = 'tl-mine-toggle' }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, openUp: false });
  const ref = useRef(null);

  const toggleOpen = () => {
    if (!open && ref.current) {
      setDropPos(computeDropdownPos(ref.current, 170));
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} style={{ flexShrink: 0 }}>
      <button
        type="button"
        className={`${btnClass}${value ? ' active' : ''}`}
        onClick={toggleOpen}
        style={{ gap: 6 }}
      >
        {selected
          ? <><span style={{ width: 8, height: 8, borderRadius: '50%', background: selected.dot, display: 'inline-block', flexShrink: 0 }} />{selected.label}</>
          : label}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, opacity: 0.6 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'fixed', top: dropPos.top, bottom: dropPos.bottom, left: dropPos.left, zIndex: 9999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '4px 0', width: 170, maxHeight: dropPos.maxHeight, overflowY: 'auto',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}>
          <FilterOption label="All" isSelected={!value} onClick={() => { onChange(''); setOpen(false); }} />
          {options.map(o => (
            <FilterOption
              key={o.value}
              label={o.label}
              dot={o.dot}
              isSelected={value === o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function MultiFilterDropdown({ label, options, value, onToggle, onClear, btnClass = 'tl-mine-toggle' }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, openUp: false });
  const ref = useRef(null);

  const toggleOpen = () => {
    if (!open && ref.current) {
      setDropPos(computeDropdownPos(ref.current, 170));
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const selectedOpts = options.filter(o => value.includes(o.value));
  const btnLabel = selectedOpts.length === 0
    ? label
    : selectedOpts.length === 1
      ? selectedOpts[0].label
      : `${label} (${selectedOpts.length})`;

  return (
    <div ref={ref} style={{ flexShrink: 0 }}>
      <button
        type="button"
        className={`${btnClass}${value.length ? ' active' : ''}`}
        onClick={toggleOpen}
        style={{ gap: 6 }}
      >
        {selectedOpts.length === 1
          ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: selectedOpts[0].dot, display: 'inline-block', flexShrink: 0 }} />
          : null}
        {btnLabel}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, opacity: 0.6 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'fixed', top: dropPos.top, bottom: dropPos.bottom, left: dropPos.left, zIndex: 9999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '4px 0', width: 170, maxHeight: dropPos.maxHeight, overflowY: 'auto',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}>
          <FilterOption label="All" isSelected={!value.length} onClick={() => { onClear(); setOpen(false); }} />
          {options.map(o => (
            <FilterOption
              key={o.value}
              label={o.label}
              dot={o.dot}
              isSelected={value.includes(o.value)}
              checkbox
              onClick={() => onToggle(o.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const DATE_PRESETS = [
  { value: 'overdue',    label: 'Overdue'    },
  { value: 'today',      label: 'Today'      },
  { value: 'this_week',  label: 'This Week'  },
  { value: 'next_week',  label: 'Next Week'  },
  { value: 'this_month', label: 'This Month' },
];
export const DATE_PRESETS_NO_OVERDUE = DATE_PRESETS.filter(p => p.value !== 'overdue');
export const DATE_PRESETS_PAST = DATE_PRESETS.filter(p => p.value !== 'overdue' && p.value !== 'next_week');

// fieldOptions/fieldValue/onFieldChange are optional — when passed, a small
// segmented control ("Start Date" / "End Date" etc.) appears at the top of
// the dropdown so the same preset/custom range can target either column.
// Existing callers that omit them are unaffected.
export function DateRangeFilterDropdown({ label, value, onChange, btnClass = 'tl-tf-chip', presets = DATE_PRESETS, fieldOptions, fieldValue, onFieldChange }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, openUp: false });
  const [showCustom, setShowCustom] = useState(value.preset === 'custom');
  const [customFrom, setCustomFrom] = useState(value.from || '');
  const [customTo, setCustomTo] = useState(value.to || '');
  const ref = useRef(null);

  const toggleOpen = () => {
    if (!open && ref.current) {
      setDropPos(computeDropdownPos(ref.current, 210, 320));
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const selectPreset = (preset) => {
    onChange({ preset, from: '', to: '' });
    setShowCustom(false);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom && !customTo) return;
    onChange({ preset: 'custom', from: customFrom, to: customTo });
    setOpen(false);
  };

  const clear = () => {
    onChange({ preset: '', from: '', to: '' });
    setCustomFrom('');
    setCustomTo('');
    setShowCustom(false);
    setOpen(false);
  };

  const presetLabel = presets.find(p => p.value === value.preset)?.label;
  const rangeLabel = value.preset === 'custom'
    ? (value.from && value.to
        ? `${formatDate(value.from)} – ${formatDate(value.to)}`
        : value.from ? `From ${formatDate(value.from)}` : value.to ? `Until ${formatDate(value.to)}` : null)
    : presetLabel || null;
  const fieldLabel = fieldOptions?.find(f => f.value === fieldValue)?.label;
  const btnLabel = fieldOptions
    ? (rangeLabel ? `${fieldLabel}: ${rangeLabel}` : fieldLabel || label)
    : (rangeLabel || label);

  return (
    <div ref={ref} style={{ flexShrink: 0 }}>
      <button type="button" className={`${btnClass}${value.preset ? ' active' : ''}`} onClick={toggleOpen} style={{ gap: 6 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{btnLabel}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, opacity: 0.6 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'fixed', top: dropPos.top, bottom: dropPos.bottom, left: dropPos.left, zIndex: 9999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '4px 0', width: 210,
          maxHeight: dropPos.maxHeight, overflowY: 'auto',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}>
          {fieldOptions && (
            <div style={{
              display: 'flex', gap: 4, padding: '6px 8px 8px',
              borderBottom: '1px solid var(--border)', marginBottom: 4,
            }}>
              {fieldOptions.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => onFieldChange(f.value)}
                  style={{
                    flex: 1, fontSize: 11.5, fontWeight: 600, padding: '5px 8px', borderRadius: 6,
                    border: `1px solid ${fieldValue === f.value ? 'var(--brand)' : 'var(--border)'}`,
                    background: fieldValue === f.value ? 'var(--bg-muted)' : 'transparent',
                    color: fieldValue === f.value ? 'var(--brand)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <FilterOption label="All" isSelected={!value.preset} onClick={clear} />
          {presets.map(p => (
            <FilterOption key={p.value} label={p.label} isSelected={value.preset === p.value} onClick={() => selectPreset(p.value)} />
          ))}
          <FilterOption label="Custom Range…" isSelected={value.preset === 'custom'} onClick={() => setShowCustom(s => !s)} />
          {showCustom && (
            <div style={{ padding: '8px 14px 10px', display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--border)', marginTop: 4 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
                From
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  style={{ display: 'block', width: '100%', marginTop: 2, padding: '5px 7px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                />
              </label>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
                To
                <input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  style={{ display: 'block', width: '100%', marginTop: 2, padding: '5px 7px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                />
              </label>
              <button type="button" className="btn-primary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={applyCustom}>Apply</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FilterOption({ label, dot, isSelected, checkbox, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '7px 14px', fontSize: 13, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8,
        color: isSelected ? 'var(--brand)' : 'var(--text-secondary)',
        fontWeight: isSelected ? 600 : 400,
        background: hov || isSelected ? 'var(--bg-muted)' : 'transparent',
      }}
    >
      {checkbox && (
        <input type="checkbox" checked={isSelected} readOnly style={{ flexShrink: 0, pointerEvents: 'none' }} />
      )}
      {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
      {label}
    </div>
  );
}
